/*!
 * ETAR theme v4.2.4 — Reviews ring
 * Developed by FACTICIED · https://www.facticied.com
 */
(() => {
  if (customElements.get('et-reviews')) return;
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class EtReviews extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const opt = { signal: this.ac.signal };
      this.stage = this.querySelector('[data-stage]');
      this.ring = this.querySelector('[data-ring]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.dots = Array.from(this.querySelectorAll('.et-rv__dots i'));
      this.n = this.cards.length;
      if (!this.n) return;
      this.step = 360 / this.n;
      this.rot = 0; this.target = null;
      this.speed = (parseFloat(this.dataset.speed) || 0) * 2.2; // degrees per second

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1), opt);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1), opt);
      this.addEventListener('pointerenter', () => { this.hover = true; }, opt);
      this.addEventListener('pointerleave', () => { this.hover = false; this.loop(); }, opt);

      // Drag to turn
      let x0 = null, r0 = 0;
      this.stage.addEventListener('pointerdown', (e) => { if (!this.isRing) return; x0 = e.clientX; r0 = this.rot; this.target = null; this.stage.setPointerCapture?.(e.pointerId); }, opt);
      this.stage.addEventListener('pointermove', (e) => { if (x0 === null) return; this.rot = r0 + (e.clientX - x0) * 0.25; this.paint(); }, opt);
      const up = () => { if (x0 === null) return; x0 = null; this.target = Math.round(this.rot / this.step) * this.step; this.loop(); };
      this.stage.addEventListener('pointerup', up, opt);
      this.stage.addEventListener('pointercancel', up, opt);

      // Flat row: keep dots in sync
      this.ring.addEventListener('scroll', () => {
        if (this.isRing) return;
        const mid = this.ring.scrollLeft + this.ring.clientWidth / 2;
        let best = 0, bd = Infinity;
        this.cards.forEach((c, i) => { const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid); if (d < bd) { bd = d; best = i; } });
        this.mark(best);
      }, { passive: true, signal: this.ac.signal });

      document.addEventListener('shopify:block:select', (e) => {
        const i = this.cards.indexOf(e.target);
        if (i < 0) return;
        if (this.isRing) { this.target = -i * this.step; this.loop(); } else e.target.scrollIntoView({ inline: 'center', block: 'nearest' });
        this.hover = true;
      }, opt);
      document.addEventListener('shopify:block:deselect', () => { this.hover = false; this.loop(); }, opt);

      this.vio = new IntersectionObserver((en) => { this.inView = en[0].isIntersecting; if (this.inView) this.loop(); }, { threshold: 0 });
      this.vio.observe(this);
      this.ro = new ResizeObserver(() => this.layout());
      this.ro.observe(this);
      mqMobile.addEventListener('change', () => this.layout(), opt);
      this.layout();
    }

    disconnectedCallback() { this.ac?.abort(); this.vio?.disconnect(); this.ro?.disconnect(); cancelAnimationFrame(this.raf); }

    layout() {
      const html = document.documentElement;
      this.isRing = this.n >= 4 && !mqMobile.matches && !mqReduce.matches && html.classList.contains('et-3d');
      this.classList.toggle('is-flat', !this.isRing);
      if (!this.isRing) { this.cards.forEach((c) => c.style.removeProperty('--f')); return; }
      const w = this.cards[0].offsetWidth;
      const r = Math.max(w * 0.9, (w / 2) / Math.tan(Math.PI / this.n) + 40);
      this.style.setProperty('--r', r.toFixed(0) + 'px');
      const h = Math.max(...this.cards.map((c) => c.offsetHeight));
      this.style.setProperty('--rv-h', h + 'px');
      this.paint();
    }

    go(dir) {
      if (!this.isRing) {
        const c = this.cards[0];
        this.ring.scrollBy({ left: dir * (c.offsetWidth + 18), behavior: 'smooth' });
        return;
      }
      const base = this.target ?? Math.round(this.rot / this.step) * this.step;
      this.target = base - dir * this.step;
      this.loop();
    }

    loop() {
      if (this.raf || !this.isRing) return;
      let last = performance.now();
      const tick = (now) => {
        this.raf = 0;
        const dt = Math.min(0.05, (now - last) / 1000); last = now;
        if (this.target !== null) {
          this.rot += (this.target - this.rot) * Math.min(1, dt * 7);
          if (Math.abs(this.target - this.rot) < 0.05) { this.rot = this.target; this.target = null; }
        } else if (this.speed > 0 && !this.hover && !mqReduce.matches) {
          this.rot -= this.speed * dt;
        }
        this.paint();
        const moving = this.target !== null || (this.speed > 0 && !this.hover);
        if (this.inView && moving) this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    paint() {
      this.style.setProperty('--rot', this.rot.toFixed(2) + 'deg');
      let best = 0, bf = -2;
      this.cards.forEach((c, i) => {
        const a = ((i * this.step + this.rot) % 360 + 360) % 360;
        const f = Math.cos((a * Math.PI) / 180);
        c.style.setProperty('--f', Math.max(0, f).toFixed(3));
        if (f > bf) { bf = f; best = i; }
      });
      this.mark(best);
    }

    mark(i) { if (i === this.front) return; this.front = i; this.dots.forEach((d, n) => d.classList.toggle('is-on', n === i)); }
  }
  customElements.define('et-reviews', EtReviews);
})();
